import { Grid } from '@mui/material'
import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getLocalizedRoleName } from '../../../common/roleHelper'
import { ILinkedSkill, IPersonProjectInfo } from '../../../interfaces/projects/IProjects'
import { useStore } from '../../../Provider'
import { OPLoadingCircle } from '../../OPcomponents/OPLoadingcircle'
import ProfileEmployersJobTitlesWorkHistory from './ProfileWorkHistory/ProfileEmployersJobTitlesWorkHistory'
import ProfilePrivateWorkHistory from './ProfileWorkHistory/ProfilePrivateWorkHistory'
import ProfilePublicWorkHistory from './ProfileWorkHistory/ProfilePublicWorkHistory'

interface IProps {
  userId: string
  userName?: string
}

const ProfileWorkExperience = (props: IProps): JSX.Element => {
  const { userId, userName } = props
  const { t } = useTranslation()
  const rootStore = useStore()
  const {
    getPersonPrivateProjects,
    getPersonPublicProjects,
    filteredPrivateWorkHistories,
    privateWorkHistoryLoading,
    publicWorkHistory,
    publicWorkHistoryLoading,
    filteredSkills,
    getPersonSkills,
    getPersonEmployersJobTitlesWorkHistory,
    employersJobTitlesWorkHistoryLoading,
    filteredEmployersJobTitlesWorkHistories,
    employersJobTitlesWorkHistorySortColumn,
    employersJobTitlesWorkHistorySortDirection,
    setEmployerJobTitlesWorkHistorySortColumn,
    toggleEmployerJobTitlesWorkHistorySortDirection,
  } = rootStore.profileStore

  const { filteredProjects, getAllProjects, loadingInitial } = rootStore.projectStore

  const onRowClick = async (id: number | string): Promise<IPersonProjectInfo | null> => {
    const personProject = await rootStore.projectStore.getDetailedUserProjects(Number(userId), Number(id))
    const publicPrivateProjectInfo =
      publicWorkHistory?.find((p) => p.projectId === id) ?? filteredPrivateWorkHistories?.find((p) => p.projectId === id)
    const employerProjectInfo = filteredEmployersJobTitlesWorkHistories?.find((p) => p.projectId === id)

    if (personProject && (publicPrivateProjectInfo || employerProjectInfo)) {
      return {
        personInfo: {
          projectName: personProject?.projectName,
          projectId: personProject?.projectId,
          startDate: personProject?.startDate?.toString(),
          endDate: personProject?.endDate?.toString(),
          descriptionFi: personProject?.descriptionFi,
          descriptionEng: personProject?.descriptionEng,
          workAmount: personProject?.expDays,
          roles: personProject?.roles.map((role) => getLocalizedRoleName(role)).join(', '),
          projectSkills: personProject?.linkedSkills.map((s: ILinkedSkill) => {
            const skillInfo = filteredSkills.find((skill) => skill.skillId === s.skillId)
            return {
              skillNameFi: skillInfo?.skillNameFi ?? '',
              skillNameEng: skillInfo?.skillNameEng ?? null,
              skillLevel: skillInfo?.skillLevel,
              skillComment: skillInfo?.comment,
            }
          }),
        },
        projectInfo: {
          name: (publicPrivateProjectInfo || employerProjectInfo)?.projectName || '',
          customer: (publicPrivateProjectInfo || employerProjectInfo)?.customer,
          description: publicPrivateProjectInfo?.description || employerProjectInfo?.description || '',
          descriptionEng: publicPrivateProjectInfo?.descriptionEng || employerProjectInfo?.descriptionEng || '',
          projectId: (publicPrivateProjectInfo || employerProjectInfo)?.projectId || 0,
          startDate: publicPrivateProjectInfo?.startDate || employerProjectInfo?.empDetails?.[0]?.startDate,
          endDate: publicPrivateProjectInfo?.endDate || employerProjectInfo?.empDetails?.[0]?.endDate,
          projectOwner: publicPrivateProjectInfo?.projectOwner || 0,
          branchName: publicPrivateProjectInfo?.branchName || '',
          contactPerson: publicPrivateProjectInfo?.contactPerson || '',
          approved: true,
          employerId: publicPrivateProjectInfo?.employerId || employerProjectInfo?.empDetails?.[0]?.employerId || 0,
        },
      }
    }
    return null
  }

  useEffect(() => {
    async function fetchData() {
      if (filteredProjects.length === 0) {
        await getAllProjects(Number(userId))
      }
      if (filteredSkills.length === 0) {
        await getPersonSkills(Number(userId))
      }
      await Promise.allSettled([
        getPersonPrivateProjects(Number(userId)),
        getPersonPublicProjects(Number(userId)),
        getPersonEmployersJobTitlesWorkHistory(Number(userId)),
      ])
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intention: run once on mount to mirror previous logic
  }, [])

  const columnMap: Record<string, string> = {
    [t('Skills.EmployerJob')]: 'projectName',
    [t('Projects.ProjectStartDate')]: 'empDetails[0].startDate',
    [t('Projects.ProjectEndDate')]: 'empDetails[0].endDate',
    [t('WorkHistory.Expirience')]: 'daysWorked',
    [t('WorkHistory.Skills')]: 'linkedSkills.length',
  }

  const onEmployersSortRequest = (headerText: string) => {
    const mapped = columnMap[headerText]
    if (!mapped) return
    if (employersJobTitlesWorkHistorySortColumn === mapped) {
      toggleEmployerJobTitlesWorkHistorySortDirection()
    } else {
      setEmployerJobTitlesWorkHistorySortColumn(mapped)
    }
  }

  return (
    <Grid container spacing={5} className='gridcontainer--formcontainer__addnewpaddings'>
      {publicWorkHistoryLoading || employersJobTitlesWorkHistoryLoading || loadingInitial ? (
        <Grid item xs={12}>
          <OPLoadingCircle />
        </Grid>
      ) : (
        <>
          <ProfileEmployersJobTitlesWorkHistory
            employersJobTitlesWorkHistory={filteredEmployersJobTitlesWorkHistories}
            loading={employersJobTitlesWorkHistoryLoading}
            onRowClick={onRowClick}
            orderBy={Object.keys(columnMap).find((k) => columnMap[k] === employersJobTitlesWorkHistorySortColumn) || ''}
            order={employersJobTitlesWorkHistorySortDirection === 'ascending' ? 'asc' : 'desc'}
            onSortRequest={onEmployersSortRequest}
            userName={userName}
          />
          <ProfilePublicWorkHistory
            publicWorkHistory={publicWorkHistory}
            publicWorkHistoryLoading={publicWorkHistoryLoading}
            onRowClick={onRowClick}
          />
          <ProfilePrivateWorkHistory
            privateWorkHistory={filteredPrivateWorkHistories}
            privateWorkHistoryLoading={privateWorkHistoryLoading}
            onRowClick={onRowClick}
            excludeProjectIds={filteredEmployersJobTitlesWorkHistories.map((p) => p.projectId)}
          />
        </>
      )}
    </Grid>
  )
}

export default observer(ProfileWorkExperience)
